import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { API, formatApiError } from "../lib/api";
import { Flame, Loader2, MailCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const Shell = ({ children }) => (
  <div className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10 overflow-hidden">
    <div className="pointer-events-none absolute inset-0 opacity-[0.12] bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1654198340681-a2e0fc449f1b?crop=entropy&cs=srgb&fm=jpg&q=70&w=1200')" }} />
    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(600px_400px_at_50%_0%,rgba(0,102,255,0.15),transparent_60%)]" />
    <div className="relative w-full max-w-md fade-up">
      <Link to="/login" className="flex items-center justify-center gap-2 mb-8">
        <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0066FF] to-[#0047B3] flex items-center justify-center shadow-[0_0_20px_rgba(0,102,255,0.5)]"><Flame className="text-white" size={22} strokeWidth={2.5} fill="white" /></span>
        <span className="font-heading font-black text-3xl uppercase tracking-tighter text-white">Kink<span className="bg-gradient-to-r from-[#0066FF] to-[#00E5FF] bg-clip-text text-transparent">Vault</span></span>
      </Link>
      <div className="border border-white/10 bg-[#0A0A0A] p-8 rounded-2xl shadow-[0_30px_80px_-30px_rgba(0,0,0,1),inset_0_1px_0_rgba(255,255,255,0.05)]">{children}</div>
    </div>
  </div>
);

const inputCls = "w-full bg-[#141414] border border-[#1C1C1F] text-white px-4 py-3 focus:outline-none focus:border-[#00E5FF] transition-colors placeholder:text-[#52525B] rounded-xl";
const btnCls = "w-full bg-[#00E5FF] text-black font-heading uppercase tracking-[0.2em] text-sm py-3.5 rounded-xl hover:bg-[#00c9e0] transition-colors disabled:opacity-50 flex items-center justify-center gap-2";

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <Shell>
        <div className="text-center" data-testid="forgot-success">
          <div className="w-14 h-14 rounded-full bg-[#00E5FF]/15 flex items-center justify-center mx-auto mb-4">
            <MailCheck className="text-[#00E5FF]" size={26} />
          </div>
          <h1 className="font-heading text-2xl uppercase tracking-tight text-white mb-2">Check your inbox</h1>
          <p className="text-[#A1A1AA] text-sm leading-relaxed mb-6">If an account exists for <span className="text-white">{email}</span>, we've sent a link to reset your password. It expires in 30 minutes.</p>
          <Link to="/login" className="text-[#00E5FF] hover:text-[#00c9e0] font-semibold text-sm inline-flex items-center gap-1"><ArrowLeft size={15} /> Back to log in</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="font-heading text-3xl uppercase tracking-tight text-white mb-1">Forgot password</h1>
      <p className="text-[#A1A1AA] text-sm mb-6">Enter your email and we'll send you a reset link.</p>
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required className={inputCls} />
        <button data-testid="forgot-submit" disabled={loading} className={btnCls}>
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Send reset link"}
        </button>
      </form>
      <p className="text-center text-sm text-[#A1A1AA] mt-6">
        <Link to="/login" className="text-[#00E5FF] hover:text-[#00c9e0] font-semibold inline-flex items-center gap-1"><ArrowLeft size={14} /> Back to log in</Link>
      </p>
    </Shell>
  );
}

function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters");
    if (password !== confirm) return setError("Passwords don't match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — log in with your new password");
      nav("/login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <h1 className="font-heading text-3xl uppercase tracking-tight text-white mb-1">Set a new password</h1>
      <p className="text-[#A1A1AA] text-sm mb-6">Choose a new password for your account.</p>
      {!token && <div className="bg-[#0A2540] border border-[#00E5FF] text-[#ff6666] text-sm px-4 py-2.5 mb-4 font-mono rounded-lg" data-testid="reset-no-token">Missing or invalid reset link. Request a new one from the login page.</div>}
      {error && <div data-testid="reset-error" className="bg-[#0A2540] border border-[#00E5FF] text-[#ff6666] text-sm px-4 py-2.5 mb-4 font-mono rounded-lg">{error}</div>}
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" required disabled={!token} className={inputCls} />
        <input data-testid="reset-password-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" required disabled={!token} className={inputCls} />
        <button data-testid="reset-submit" disabled={loading || !token} className={btnCls}>
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Update password"}
        </button>
      </form>
      <p className="text-center text-sm text-[#A1A1AA] mt-6">
        <Link to="/login" className="text-[#00E5FF] hover:text-[#00c9e0] font-semibold inline-flex items-center gap-1"><ArrowLeft size={14} /> Back to log in</Link>
      </p>
    </Shell>
  );
}

export default function Auth({ mode }) {
  if (mode === "forgot") return <ForgotPassword />;
  if (mode === "reset") return <ResetPassword />;
  if (mode === "verify") return <VerifyEmail />;

  const isLogin = mode === "login";
  return <LoginRegister isLogin={isLogin} />;
}

function VerifyEmail() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!token) { setStatus("error"); setMsg("This verification link is missing its token."); return; }
    api.post("/auth/verify-email", { token })
      .then(() => { setStatus("ok"); refresh?.(); })
      .catch((e) => { setStatus("error"); setMsg(formatApiError(e.response?.data?.detail) || "Verification failed."); });
  }, [token]); // eslint-disable-line

  return (
    <Shell>
      <div className="text-center" data-testid={`verify-${status}`}>
        {status === "loading" && (
          <>
            <Loader2 className="text-[#00E5FF] animate-spin mx-auto mb-4" size={30} />
            <p className="text-[#A1A1AA] text-sm">Confirming your email…</p>
          </>
        )}
        {status === "ok" && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#00E5FF]/15 flex items-center justify-center mx-auto mb-4"><MailCheck className="text-[#00E5FF]" size={26} /></div>
            <h1 className="font-heading text-2xl uppercase tracking-tight text-white mb-2">Email confirmed</h1>
            <p className="text-[#A1A1AA] text-sm mb-6">Your account is verified. Welcome to the vault.</p>
            <Link to="/" className="inline-block bg-[#00E5FF] text-black font-heading uppercase tracking-[0.2em] text-xs px-6 py-3 rounded-xl hover:bg-[#00c9e0] transition-colors">Enter KinkVault</Link>
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="font-heading text-2xl uppercase tracking-tight text-white mb-2">Link didn't work</h1>
            <p className="text-[#ff6666] text-sm mb-6 font-mono">{msg}</p>
            <Link to="/login" className="text-[#00E5FF] hover:text-[#00c9e0] font-semibold text-sm inline-flex items-center gap-1"><ArrowLeft size={15} /> Back to log in</Link>
          </>
        )}
      </div>
    </Shell>
  );
}

function LoginRegister({ isLogin }) {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [params] = useSearchParams();

  useEffect(() => {
    const err = params.get("error");
    if (err) toast.error(`${err === "google" ? "Google" : "Discord"} sign-in failed. Try again.`);
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) await login(email, password);
      else await register(email, password, username);
      toast.success(isLogin ? "Welcome back" : "Account created");
      nav("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const oauth = (p) => { window.location.href = `${API}/auth/${p}/start`; };

  return (
    <Shell>
      <h1 className="font-heading text-3xl uppercase tracking-tight text-white mb-1">{isLogin ? "Log in" : "Join the vault"}</h1>
      <p className="text-[#A1A1AA] text-sm mb-6">{isLogin ? "Welcome back, freak." : "Create your account. 18+ only."}</p>

      {error && <div data-testid="auth-error" className="bg-[#0A2540] border border-[#00E5FF] text-[#ff6666] text-sm px-4 py-2.5 mb-4 font-mono rounded-lg">{error}</div>}

      <form onSubmit={submit} className="space-y-4">
        {!isLogin && (
          <input data-testid="auth-username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" required className={inputCls} />
        )}
        <input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required className={inputCls} />
        <input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" required className={inputCls} />
        {isLogin && (
          <div className="text-right -mt-1">
            <Link to="/forgot-password" data-testid="auth-forgot-link" className="text-[#00E5FF] hover:text-[#00c9e0] text-xs font-semibold">Forgot password?</Link>
          </div>
        )}
        <button data-testid="auth-submit" disabled={loading} className={btnCls}>
          {loading ? <Loader2 className="animate-spin" size={18} /> : isLogin ? "Log in" : "Create account"}
        </button>
      </form>

      <div className="flex items-center gap-3 my-5">
        <span className="flex-1 h-px bg-[#1C1C1F]" />
        <span className="font-mono text-[10px] text-[#52525B] uppercase">or</span>
        <span className="flex-1 h-px bg-[#1C1C1F]" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <button data-testid="oauth-google" onClick={() => oauth("google")} className="flex items-center justify-center gap-2 border border-[#1C1C1F] text-[#A1A1AA] hover:text-white hover:border-[#2A2A2E] transition-colors py-2.5 font-mono text-xs uppercase rounded-lg">
          <svg width="15" height="15" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.8v3.6h5.1c-.2 1.3-1.6 3.9-5.1 3.9-3.1 0-5.6-2.5-5.6-5.7s2.5-5.7 5.6-5.7c1.8 0 2.9.7 3.6 1.4l2.5-2.4C16.5 3.9 14.5 3 12 3 6.9 3 3 7 3 12s3.9 9 9 9c5.2 0 8.6-3.6 8.6-8.7 0-.6-.1-1-.2-1.5H12z"/></svg>
          Google
        </button>
        <button data-testid="oauth-discord" onClick={() => oauth("discord")} className="flex items-center justify-center gap-2 border border-[#1C1C1F] text-[#A1A1AA] hover:text-white hover:border-[#2A2A2E] transition-colors py-2.5 font-mono text-xs uppercase rounded-lg">
          <svg width="15" height="15" viewBox="0 0 24 24"><path fill="#5865F2" d="M20.3 4.4A19.8 19.8 0 0 0 15.4 3l-.3.5c1.7.4 2.9 1 4.1 1.8a16.6 16.6 0 0 0-14.4 0C6 4.5 7.2 3.9 8.9 3.5L8.6 3a19.8 19.8 0 0 0-4.9 1.4C1.3 8 0.6 11.5.9 14.9A19.9 19.9 0 0 0 6.9 18l.5-.7c-1-.3-2-.8-2.8-1.4l.5-.4a14.2 14.2 0 0 0 12 0l.5.4c-.9.6-1.8 1-2.8 1.4l.5.7a19.9 19.9 0 0 0 6-3.1c.4-4-.7-7.4-3-10.5zM8.5 13c-1 0-1.7-.9-1.7-1.9s.8-1.9 1.7-1.9 1.7.9 1.7 1.9-.7 1.9-1.7 1.9zm7 0c-1 0-1.7-.9-1.7-1.9s.8-1.9 1.7-1.9 1.7.9 1.7 1.9-.7 1.9-1.7 1.9z"/></svg>
          Discord
        </button>
      </div>

      <p className="text-center text-sm text-[#A1A1AA] mt-6">
        {isLogin ? "No account? " : "Already in? "}
        <Link to={isLogin ? "/register" : "/login"} className="text-[#00E5FF] hover:text-[#00c9e0] font-semibold">{isLogin ? "Join now" : "Log in"}</Link>
      </p>
    </Shell>
  );
}
